<script setup lang="ts">
import { computed } from 'vue';

import { CustomersRatingTag } from '@apps/main/new-desktop/modules/customersRating';
import { useBrowserStore } from '@apps/main/new-desktop/core/stores';
import { HotelAmenities } from '@apps/main/new-desktop/modules/hotelAmenities';

import { AppStars, AppTag } from '@libs/ui';

import type { AlternativeHotel } from '../../../models';

interface AlternativeAccommodationCardContentProps {
  hotel: AlternativeHotel;
  ingredientIndex: number;
}

const props = defineProps<AlternativeAccommodationCardContentProps>();

const browserStore = useBrowserStore();

interface AlternativeAccommodationCardContentEmits {
  (eventName: 'show-details'): void;
  (eventName: 'show-rooms'): void;
}

const emit = defineEmits<AlternativeAccommodationCardContentEmits>();

const hasMultipleRooms = computed<boolean>(
  () => props.hotel.roomsInfo.length > 1
);
</script>

<template>
  <div
    role="button"
    tabindex="0"
    class="alternative-accommodation-card-content"
    :class="{ mobile: browserStore.isSmallerThan600px }"
    @click.stop="emit('show-details')"
    @keydown.enter.stop="emit('show-details')"
  >
    <div class="info-container">
      <span class="hotel-name">
        {{ props.hotel.hotelName }}
      </span>

      <span class="polygon-name">
        {{ props.hotel.polygonName }}
      </span>

      <div class="info-wrapper">
        <AppStars size="18" :amount="props.hotel.stars" />
        <CustomersRatingTag
          v-if="props.hotel.guestRating"
          :guest-rating="props.hotel.guestRating"
        />
        <AppTag v-if="props.hotel.mealName" type="grey" size="s">
          <template #title>
            <span class="tag-text">
              {{ props.hotel.mealName }}
            </span>
          </template>
        </AppTag>
      </div>
      <HotelAmenities :amenities="props.hotel.amenities" />
    </div>

    <button class="rooms-container" @click.stop="emit('show-rooms')">
      <div
        v-for="element in props.hotel.roomsInfo"
        :key="element.label + element.description"
        :class="['description', { 'single-room': !hasMultipleRooms }]"
      >
        <span v-if="hasMultipleRooms" class="label">
          {{ element.label }}
        </span>
        {{ element.description }}
      </div>
    </button>
  </div>
</template>

<style scoped lang="scss">
.alternative-accommodation-card-content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-12);
  justify-content: space-between;
  height: 100%;

  &.mobile {
    padding-bottom: var(--spacing-12);
    .info-container {
      gap: 0;
    }

    .info-wrapper {
      margin: var(--spacing-4) 0 var(--spacing-8) 0;
    }
  }

  .info-container {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-8);

    .hotel-name {
      color: var(--color-soft-black);
      @include font-label-m-bold;
      line-height: var(--line-heights-16);

      display: -webkit-box;
      max-width: var(--card-width);
      line-clamp: 2;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .polygon-name {
      @include font-label-s-light;
      color: var(--color-neutral-70);
    }

    .info-wrapper {
      display: flex;
      flex-wrap: wrap;
      gap: var(--spacing-8);

      .tag-text {
        @include font-label-s-bold;
      }
    }
  }

  .rooms-container {
    all: unset;
    padding: var(--spacing-12);
    background: transparent;
    border: 1px solid var(--color-scrollbar);
    border-radius: var(--border-radius-8);

    .description {
      @include font-label-s-light;

      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      width: 0;
      min-width: 100%;

      .label {
        @include font-label-s-bold;
        white-space: nowrap;
      }

      &.single-room {
        white-space: normal;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        line-clamp: 2;
        -webkit-line-clamp: 2;
        line-height: var(--line-heights-14);
      }
    }
  }
}
</style>
